import React, {useState, useEffect} from 'react'
import {Helmet} from 'react-helmet'
import {
    Box,
    FormControl,
    FormLabel,
    Text,
    Input,
    Select,
    Button,
    Heading,
    Spinner,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink, useHistory} from 'react-router-dom'

import {useIntl} from 'react-intl'
import useSitePreference from '../../hooks/use-site-preference'
import {RadiusInMiles} from '../../static/constants'
import {
    selectOptionStyles,
    stateContentStyles,
    storeDetailsButton,
    storeHoursStyles,
    storeLocatorBanner,
    storeSelectorButton
} from './index.styles'
import {useAccessToken, useSearchStores} from '@salesforce/commerce-sdk-react'
import {pxToRem} from '../../utills/pxToRem'
import {useLocation} from 'react-router-dom'
import useCustomFetch from '../../hooks/useFetchContent'
import Breadcrumbs from '../../components/breadcrumbs'
import getLatLang from '../../components/store-selector-modal/getLatLong'
import StoreHoursTable from '../../components/store-selector-modal/storeHoursTable'
import {LocationIcon} from '../../components/icons'
import GoogleMaps from '../../components/google-map'
import size from '../../utills/size'
import {fonts} from '../../theme/foundations/fonts'

const slotIDs = 'storelocator-banner'

const StoreLocator = () => {
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const selectedState = searchParams.get('dwfrm_storelocator_state')
    const [zipCode, setZipCode] = useState('')
    const [zipCodeIsValid, setZipCodeIsValid] = useState(true)
    const [state, setState] = useState('')
    const [states, setStates] = useState([])
    const [miles, setMiles] = useState('')
    const [findStoreError, setFindStoreError] = useState('')
    const [checkErrors] = useState(false)
    const [enableSearchStore, setEnableSearchStore] = useState(false)
    const [selectedStateName, setSElectedStateName] = useState('')
    const [storeList, setStoreList] = useState([])
    const [searchIn, setSearchIn] = useState('')
    const {data: defaultLocationData} = useSitePreference('defaultStoreLatLong')
    const [defaultLatLng, setDefaultLatLng] = useState({lat: null, lng: null})

    const intl = useIntl()
    const {token} = useAccessToken()
    const history = useHistory()

    const [parameters, setParameters] = useState()

    // api hook for seearch stores
    const {data: storeData, isLoading} = useSearchStores(
        {
            parameters: parameters
        },
        {
            enabled: enableSearchStore
        }
    )

    // call site preference api to get states list and maxDistance for search stores api
    const {data} = useSitePreference('statesMapping,storeLookupMaxDistance')
    const maxDistance = data?.storeLookupMaxDistance
    const statesList = data?.statesMapping
    const {response, loading} = useCustomFetch(token, slotIDs, true)
    const stateSontentId = state.length > 0 ? `storelocator_${state}_content` : null

    const {response: stateBasedContent} = useCustomFetch(token, stateSontentId, false)

    const validateZipCode = (value) => {
        const postalPattern =
            /(^\d{5}(-\d{4})?$)|(^[abceghjklmnprstvxyABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Za-z]{1} *\d{1}[A-Za-z]{1}\d{1}$)/
        let isValid = true
        if (value?.length == 0) {
            isValid = true
            setZipCodeIsValid(true)
        } else {
            isValid = postalPattern.test(value)
            setZipCodeIsValid(postalPattern.test(value))
        }
        return isValid
    }
    const [allStatesWithStores, setAllStatesWithStores] = useState([])
    const [masterStatesWithStores, setMasterStatesWithStores] = useState([])

    const transformStatesList = (statesList) => {
        let states = []
        for (let key in statesList) {
            if (statesList && statesList[key]) {
                states.push({code: key, name: statesList[key]})
            }
        }
        setStates(states)
    }

    const getStatesFromStores = (stores) => {
        if (!stores || stores.length === 0) return []
        const storeStatesSet = new Set(stores.map((store) => store.stateCode))
        return states.filter((state) => storeStatesSet.has(state.code))
    }

    useEffect(() => {
        if (storeData?.data && states.length > 0) {
            const allStates = getStatesFromStores(storeData.data)
            if (masterStatesWithStores.length === 0) {
                setMasterStatesWithStores(allStates)
                setAllStatesWithStores(allStates)
            } else {
                setAllStatesWithStores(masterStatesWithStores)
            }
        }
    }, [storeData, states, masterStatesWithStores])

    const onChangeZipCode = (e) => {
        setState('')
        setSElectedStateName('')
        setFindStoreError('')
        const value = e.target.value
        if (value?.length <= 5) {
            setZipCode(value)
            if (checkErrors) validateZipCode(value)
        }
    }

    useEffect(() => {
        if (!defaultLocationData?.defaultStoreLatLong) return
        setDefaultLatLng(JSON.parse(defaultLocationData.defaultStoreLatLong))
    }, [defaultLocationData])

    useEffect(() => {
        const filterType = zipCode
            ? `NEAR ${zipCode}`
            : selectedStateName
            ? `IN ${selectedStateName}`
            : 'IN USA'
        if (storeData?.data) {
            setStoreList(storeData.data)
            setSearchIn(filterType)
            setFindStoreError('')
            setZipCodeIsValid(true)
        } else if (storeData?.limit === 0) {
            setSearchIn(filterType)
            setStoreList([])
            setFindStoreError('')
            setZipCodeIsValid(true)
            if (zipCode.length === 5) {
                setFindStoreError(
                    intl.formatMessage({
                        id: 'storeSelector.invalidZip.error.msg',
                        defaultMessage: `We didn’t find a Cavender’s near that ZIP code. Try expanding your search area or entering the ZIP code of a nearby city to locate the closest store.`
                    })
                )
            }
        }
    }, [storeData])

    useEffect(() => {
        if (selectedState) {
            onChangeState(selectedState, true)
        } else if (defaultLatLng.lat != null && defaultLatLng.lng != null) {
            const params = {
                countryCode: 'US',
                latitude: defaultLatLng.lat,
                longitude: defaultLatLng.lng,
                distanceUnit: 'mi',
                limit: 200
            }
            setParameters(params)
            setEnableSearchStore(true)
        }
    }, [selectedState, states, defaultLatLng])

    const handleViewAllStores = () => {
        setState('')
        setMiles('')
        setZipCode('')
        setSElectedStateName('')
        if (selectedState) history.replace('/storelocator')
        const param = {
            countryCode: 'US',
            latitude: defaultLatLng.lat,
            longitude: defaultLatLng.lng,
            distanceUnit: 'mi',
            limit: 200
        }
        setParameters(param)
        setEnableSearchStore(true)
    }

    const onMilesChanges = (e) => {
        setState('')
        setSElectedStateName('')
        setMiles(e.target.value)
        setFindStoreError('')
    }

    const onChangeState = async (selectedSt, selectedFromParams) => {
        if (!selectedFromParams && selectedState) history.replace('/storelocator')
        setState(selectedSt)
        states.filter((st) => st.code === selectedSt)
        const selectedStateArr = states.filter((st) => st.code === selectedSt)
        const selectedName = selectedStateArr[0].name
        setZipCode('')
        setMiles('')
        setSElectedStateName(selectedName)
        setFindStoreError('')
        const address = encodeURIComponent(`${selectedSt}, US`)
        const data = await getLatLang(address)

        if (data?.success) {
            const param = {
                countryCode: 'US',
                c_stateCode: selectedSt,
                latitude: data?.lat,
                longitude: data?.long,
                distanceUnit: 'mi'
            }
            setParameters(param)
            // enable this flag to call the store search api hook
            setEnableSearchStore(true)
        } else {
            setFindStoreError('No results found')
        }
    }

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleFindStores()
        }
    }

    const breadcrumbData = {
        parentFolder: {
            folderId: 'storelocator',
            folderName: 'Find A Store',
            url: '/storelocato'
        },
        contentFolder: {
            folderId: 'storelocator',
            folderName: 'Find A Store',
            url: '/storelocator/'
        }
    }

    const getStoresData = async () => {
        const useZipCodeForGeo = zipCode !== ''
        if (selectedState) history.replace('/storelocator')
        const isValid = validateZipCode(zipCode)
        if (useZipCodeForGeo && isValid) {
            // get lat and long from google api using zipcode
            const data = await getLatLang(zipCode)
            if (data?.success) {
                const param = {
                    countryCode: 'US',
                    postalCode: zipCode,
                    maxDistance: miles ? miles : maxDistance,
                    latitude: data?.lat,
                    longitude: data?.long,
                    distanceUnit: 'mi'
                }
                if (miles.length > 0) {
                    param.maxDistance = Number(miles)
                }
                setParameters(param)
                // enable this flag to call the store search api hook
                setEnableSearchStore(true)
            } else {
                setFindStoreError(
                    intl.formatMessage({
                        id: 'storeSelector.empty.error.msg',
                        defaultMessage: `We didn’t find a Cavender’s near that ZIP code. Try expanding your search area or entering the ZIP code of a nearby city to locate the closest store.`
                    })
                )
            }
        } else {
            if (miles.length > 0) {
                const param = {
                    countryCode: 'US',
                    latitude: defaultLatLng.lat,
                    longitude: defaultLatLng.lng,
                    distanceUnit: 'mi',
                    maxDistance: Number(miles)
                }
                setParameters(param)
                setEnableSearchStore(true)
                return null
            }
            const address = encodeURIComponent(`${state}, US`)
            const data = await getLatLang(address)
            if (data?.success) {
                const param = {
                    countryCode: 'US',
                    latitude: data?.lat,
                    longitude: data?.long,
                    distanceUnit: 'mi',
                    c_stateCode: state
                }
                setParameters(param)
                setEnableSearchStore(true)
            }
        }
    }

    const handleFindStores = () => {
        const isValid = validateZipCode(zipCode)
        const fieldsHaveValidEntry = zipCode !== '' || state !== '' || miles !== ''
        if (!fieldsHaveValidEntry) {
            setFindStoreError(
                intl.formatMessage({
                    id: 'storeLocator.fieldInValid.error.msg',
                    defaultMessage: 'Please select state or enter a valid zip code to get results'
                })
            )
        } else if (!isValid) {
            setFindStoreError(
                intl.formatMessage({
                    id: 'storeSelector.invalid_zip_code.error.msg',
                    defaultMessage: `Invalid zip code.`
                })
            )
        } else {
            getStoresData()
        }
    }
    const availableResult = (count) => {
        return intl.formatMessage(
            {
                id: 'storeSelector.result_count.msg',
                defaultMessage: '{result}'
            },
            {
                result: count > 1 ? `${count} RESULTS ${searchIn}` : `${count} RESULT ${searchIn}`
            }
        )
    }

    useEffect(() => {
        if (statesList) transformStatesList(statesList)
    }, [statesList, state])

    return (
        <Box padding={'0 15px 30px'} width={'100%'} maxWidth={size.xxl} margin={'0 auto'}>
            <Helmet>
                <title>{`Store Locator | Cavender's`}</title>
                <meta name="keywords" content={`Cavender's`} />
            </Helmet>
            <Breadcrumbs breadcrumbData={breadcrumbData} />
            {loading && (
                <Spinner
                    data-testid="loading"
                    thickness="2px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="blue.600"
                    size="md"
                    position="absolute"
                    left={'50%'}
                    transform="translateY(-50%)"
                />
            )}
            {!loading && response && (
                <Box sx={storeLocatorBanner} dangerouslySetInnerHTML={{__html: response}} />
            )}

            {state.length > 0 && stateBasedContent && (
                <Box
                    sx={stateContentStyles}
                    dangerouslySetInnerHTML={{__html: stateBasedContent}}
                />
            )}
            <Box margin={[pxToRem(10), pxToRem(10), pxToRem(10), pxToRem(20)]}>
                <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    gap="8px"
                    flexDirection={['column', 'column', 'column', 'row']}
                >
                    <Box
                        display={'flex'}
                        gap={'16px'}
                        width={['100%', '100%', '100%', '35%']}
                        flexDirection={['column', 'column', 'column', 'row']}
                    >
                        <FormControl id="zip" width={['100%', '100%', '100%', '50%']}>
                            <FormLabel fontSize={'14px'} fontWeight={400}>
                                Zip Code
                            </FormLabel>
                            <Input
                                type="number"
                                value={zipCode}
                                onKeyDown={handleKeyPress}
                                onChange={onChangeZipCode}
                                placeholder="00000"
                                autoComplete="off"
                                borderRadius={0}
                                width={['100%', '100%', '100%', '90%']}
                                onWheel={(e) => e.target.blur()} // This prevents scroll from affecting the input
                                className={!zipCodeIsValid && 'inputHasError'}
                            />
                        </FormControl>

                        <FormControl
                            className="state"
                            id="miles"
                            width={['100%', '100%', '100%', '40%']}
                        >
                            <FormLabel fontSize={'14px'} fontWeight={400}>
                                Radius (miles)
                            </FormLabel>
                            <Select
                                borderRadius={0}
                                placeholder="Select"
                                value={miles}
                                onChange={onMilesChanges}
                                sx={selectOptionStyles}
                            >
                                {RadiusInMiles?.map(({miles, milesLable}) => (
                                    <option key={miles} value={miles}>
                                        {milesLable}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box
                        width={['100%', '100%', '100%', '5%']}
                        marginTop={[pxToRem(32), pxToRem(32), pxToRem(32), pxToRem(36)]}
                        fontSize={'14px'}
                        fontWeight={400}
                    >
                        or
                    </Box>
                    <Box
                        width={['100%', '100%', '100%', '25%']}
                        marginTop={[pxToRem(31), pxToRem(31), pxToRem(31), 0]}
                    >
                        <FormControl className="state" id="state">
                            <FormLabel fontSize={'14px'} fontWeight={400}>
                                State
                            </FormLabel>
                            <Select
                                borderRadius={0}
                                placeholder="State"
                                value={state}
                                onChange={(e) => onChangeState(e.target.value)}
                                sx={selectOptionStyles}
                            >
                                {allStatesWithStores?.map(({code, name}) => (
                                    <option className="options" key={code} value={code}>
                                        {name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={storeSelectorButton}>
                        <Button className="findStores button-primary" onClick={handleFindStores}>
                            {intl.formatMessage({
                                id: 'storeSelector.button.findStores.label',
                                defaultMessage: 'FIND STORES'
                            })}
                        </Button>
                    </Box>
                    <Box sx={storeSelectorButton}>
                        <Button
                            className="findStores button-tertiary"
                            onClick={handleViewAllStores}
                        >
                            {intl.formatMessage({
                                id: 'storeSelector.button.viewAllStores.label',
                                defaultMessage: 'VIEW All STORES'
                            })}
                        </Button>
                    </Box>
                </Box>
                {findStoreError && (
                    <Text
                        color={'cav_dark_red'}
                        fontSize={pxToRem(15)}
                        lineHeight={pxToRem(21)}
                        letterSpacing={pxToRem(1)}
                        margin={`${pxToRem(8)} 0`}
                    >
                        {intl.formatMessage(
                            {
                                id: 'storeSelector.error.label',
                                defaultMessage: '{error}'
                            },
                            {error: findStoreError}
                        )}
                    </Text>
                )}
            </Box>
            {!isLoading ? (
                storeList.length > 0 ? (
                    <Box display="flex" flexWrap={'wrap'} paddingTop={pxToRem(40)}>
                        <Box width={['100%', '100%', '100%', '35%', '30%']}>
                            <Box
                                marginLeft={[0, 0, 0, pxToRem(20)]}
                                marginRight={[0, 0, 0, pxToRem(30)]}
                            >
                                <Box
                                    padding={`${pxToRem(18)} ${pxToRem(10)}`}
                                    backgroundColor="cav_deep_light_gray"
                                >
                                    <Heading
                                        margin={`${pxToRem(18)} 0`}
                                        as="h2"
                                        fontSize={pxToRem(18)}
                                        letterSpacing={1}
                                        lineHeight={pxToRem(27.3)}
                                        textTransform={'uppercase'}
                                        fontFamily={fonts.primary}
                                    >
                                        {!isLoading && availableResult(storeList.length)}
                                    </Heading>
                                </Box>
                                <Box
                                    maxHeight={pxToRem(750)}
                                    overflowY={'auto'}
                                    overflowX={'hidden'}
                                >
                                    {storeList.length > 0 &&
                                        storeList.map((storeDetails) => {
                                            return (
                                                <Box
                                                    key={storeDetails.id}
                                                    borderWidth={pxToRem(1)}
                                                    borderColor={'cav_deep_light_gray'}
                                                    paddingBottom={pxToRem(23.8)}
                                                    paddingTop={pxToRem(23.8)}
                                                    paddingLeft={[
                                                        pxToRem(22.4),
                                                        pxToRem(18.2),
                                                        pxToRem(18.2),
                                                        pxToRem(22.4)
                                                    ]}
                                                >
                                                    <Box>
                                                        <Heading
                                                            as="h3"
                                                            fontSize={pxToRem(16)}
                                                            lineHeight={pxToRem(30)}
                                                            letterSpacing={pxToRem(1)}
                                                            fontWeight={700}
                                                            margin={[`${pxToRem(16)} 0`]}
                                                            fontFamily={fonts.primary}
                                                        >
                                                            {storeDetails.city}
                                                        </Heading>
                                                        <Box
                                                            float={'right'}
                                                            paddingRight={pxToRem(5)}
                                                            fontSize={pxToRem(14)}
                                                            lineHeight={pxToRem(30)}
                                                            letterSpacing={pxToRem(1)}
                                                        >
                                                            {storeDetails.distance}{' '}
                                                            {storeDetails.distanceUnit}
                                                        </Box>
                                                    </Box>
                                                    <Box
                                                        display={'flex'}
                                                        gap={pxToRem(6)}
                                                        marginBottom={pxToRem(10)}
                                                    >
                                                        <Box width={pxToRem(24)}>
                                                            <LocationIcon
                                                                width={pxToRem(18)}
                                                                height={pxToRem(18)}
                                                                color="cav_hover_brightred"
                                                            />
                                                        </Box>
                                                        <Box>
                                                            <Box
                                                                as="span"
                                                                fontWeight={700}
                                                                fontSize={pxToRem(14)}
                                                                lineHeight={pxToRem(30)}
                                                                letterSpacing={pxToRem(1)}
                                                            >
                                                                {storeDetails.name}
                                                            </Box>
                                                            <br />
                                                            <Box
                                                                as="span"
                                                                fontSize={pxToRem(14)}
                                                                lineHeight={pxToRem(30)}
                                                                letterSpacing={pxToRem(1)}
                                                            >
                                                                {storeDetails.address1}
                                                            </Box>
                                                            <br />
                                                            <Box
                                                                as="span"
                                                                fontSize={pxToRem(14)}
                                                                lineHeight={pxToRem(30)}
                                                                letterSpacing={pxToRem(1)}
                                                            >
                                                                {storeDetails.city},{' '}
                                                                {storeDetails.stateCode}{' '}
                                                                {storeDetails.postalCode}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                    {storeDetails.phone && (
                                                        <Box
                                                            display={'flex'}
                                                            gap={pxToRem(6)}
                                                            marginBottom={pxToRem(10)}
                                                        >
                                                            <Box width={pxToRem(24)}>
                                                                <LocationIcon
                                                                    width={pxToRem(18)}
                                                                    height={pxToRem(18)}
                                                                    color="cav_hover_brightred"
                                                                />
                                                            </Box>
                                                            <Box
                                                                as="a"
                                                                href={`tel:${storeDetails.phone}`}
                                                                color="black"
                                                                textDecoration="none"
                                                                fontSize={pxToRem(14)}
                                                                lineHeight={pxToRem(30)}
                                                                letterSpacing={pxToRem(1)}
                                                                _hover={{
                                                                    color: 'cav_hover_brightred'
                                                                }}
                                                            >
                                                                {storeDetails.phone}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                    <Box
                                                        sx={storeHoursStyles}
                                                        marginBottom={pxToRem(20)}
                                                    >
                                                        <Box width={pxToRem(24)}>
                                                            <LocationIcon
                                                                width={pxToRem(18)}
                                                                height={pxToRem(18)}
                                                                color="cav_hover_brightred"
                                                            />
                                                        </Box>
                                                        <Box
                                                            display={'flex'}
                                                            flexDirection={'column'}
                                                            gap={pxToRem(16)}
                                                            justifyContent={'space-between'}
                                                        >
                                                            <StoreHoursTable
                                                                hoursOnline={
                                                                    storeDetails.c_firstHoursOnline
                                                                }
                                                                hoursDefault={
                                                                    storeDetails.c_firstHoursDefault
                                                                }
                                                                hoursRows={
                                                                    storeDetails.c_firstHoursRows
                                                                }
                                                                hoursName={
                                                                    storeDetails.c_firstHoursName
                                                                }
                                                            />
                                                            <StoreHoursTable
                                                                hoursOnline={
                                                                    storeDetails.c_secondHoursOnline
                                                                }
                                                                hoursDefault={
                                                                    storeDetails.c_secondHoursDefault
                                                                }
                                                                hoursRows={
                                                                    storeDetails.c_secondHoursRows
                                                                }
                                                                hoursName={
                                                                    storeDetails.c_secondHoursName
                                                                }
                                                            />
                                                            <StoreHoursTable
                                                                hoursOnline={
                                                                    storeDetails.c_thirdHoursOnline
                                                                }
                                                                hoursDefault={
                                                                    storeDetails.c_thirdHoursDefault
                                                                }
                                                                hoursRows={
                                                                    storeDetails.c_thirdHoursRows
                                                                }
                                                                hoursName={
                                                                    storeDetails.c_thirdHoursName
                                                                }
                                                            />
                                                            <StoreHoursTable
                                                                hoursOnline={
                                                                    storeDetails.c_fourthHoursOnline
                                                                }
                                                                hoursDefault={
                                                                    storeDetails.c_fourthHoursDefault
                                                                }
                                                                hoursRows={
                                                                    storeDetails.c_fourthHoursRows
                                                                }
                                                                hoursName={
                                                                    storeDetails.c_fourthHoursName
                                                                }
                                                            />
                                                            <StoreHoursTable
                                                                hoursOnline={
                                                                    storeDetails.c_fifthHoursOnline
                                                                }
                                                                hoursDefault={
                                                                    storeDetails.c_fifthHoursDefault
                                                                }
                                                                hoursRows={
                                                                    storeDetails.c_fifthHoursRows
                                                                }
                                                                hoursName={
                                                                    storeDetails.c_fifthHoursName
                                                                }
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <Box
                                                        sx={storeDetailsButton}
                                                        marginTop={pxToRem(10)}
                                                    >
                                                        <Link
                                                            className="storeDetails button-primary"
                                                            minWidth={'inherit'}
                                                            href={storeDetails.c_gbpLink}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            _hover={{textDecoration: 'none'}}
                                                        >
                                                            {intl.formatMessage({
                                                                id: 'storeLocator.button.detDirectiom.label',
                                                                defaultMessage: 'Get Directions'
                                                            })}
                                                        </Link>
                                                    </Box>
                                                    <Box
                                                        sx={storeDetailsButton}
                                                        marginTop={pxToRem(10)}
                                                    >
                                                        <Link
                                                            className="storeDetails button-tertiary"
                                                            minWidth={'inherit'}
                                                            to={`/storedetails/?StoreID=${storeDetails.id}`}
                                                            as={RouteLink}
                                                            _hover={{textDecoration: 'none'}}
                                                        >
                                                            {intl.formatMessage({
                                                                id: 'storeLocator.button.storeDetails.label',
                                                                defaultMessage: 'Store Details'
                                                            })}
                                                        </Link>
                                                    </Box>
                                                </Box>
                                            )
                                        })}
                                </Box>
                            </Box>
                        </Box>
                        <Box width={['100%', '100%', '100%', '65%', '70%']}>
                            <GoogleMaps
                                zoom={12}
                                storeList={storeList}
                                customLat={parseFloat(storeList[0].latitude)}
                                customLong={parseFloat(storeList[0].longitude)}
                            />
                        </Box>
                    </Box>
                ) : (
                    <Text
                        textAlign={'center'}
                        color={'cav_dark_red'}
                        fontSize={pxToRem(15)}
                        lineHeight={pxToRem(21)}
                        letterSpacing={pxToRem(1)}
                        margin={pxToRem(15)}
                    >
                        {intl.formatMessage({
                            id: 'storeLocator.storelist.error.not_found',
                            defaultMessage: 'No results found'
                        })}
                    </Text>
                )
            ) : null}
        </Box>
    )
}

export default StoreLocator
